import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MockPaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  const paymentId = searchParams.get('payment_id');
  const businessId = searchParams.get('business_id');

  const handleCompletePayment = async () => {
    setProcessing(true);
    
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      await axios.post(`${API}/payments/mock-complete/${paymentId}`, {}, { withCredentials: true });
      setCompleted(true);
      toast.success('Płatność zakończona pomyślnie!');
      
      setTimeout(() => {
        navigate(`/landing/${businessId}`);
      }, 2000);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Błąd podczas przetwarzania płatności');
    } finally {
      setProcessing(false);
    }
  };

  if (!paymentId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-slate-600">Brak danych płatności</p>
            <Button onClick={() => navigate('/')} className="mt-4">Wróć na stronę główną</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {completed ? 'Płatność zakończona!' : 'Testowa płatność'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {completed ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-slate-600 mb-4">
                  Twoja strona została aktywowana!
                </p>
                <p className="text-sm text-slate-500">
                  Za chwilę zostaniesz przekierowany...
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-slate-600 mb-6">
                  To jest testowa płatność. W prawdziwej wersji zostaniesz przekierowany do Stripe lub PayU.
                </p>
                <div className="bg-slate-100 rounded-lg p-4 mb-6">
                  <p className="text-sm text-slate-500">ID płatności</p>
                  <p className="font-mono text-sm">{paymentId}</p>
                </div>
                <Button
                  onClick={handleCompletePayment}
                  disabled={processing}
                  className="w-full bg-amber-500 hover:bg-amber-600"
                  data-testid="complete-payment-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Przetwarzanie...
                    </>
                  ) : (
                    'Symuluj płatność'
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
